import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Icon_Checkmark24, Menu, TagInput, type TagInputRef } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { isEmailInvalid } from "../../../../../utils/helpers";
import { Icon_FillerSvg24 } from "./FillerSvg";
import { SelectActivator } from "./SelectActivator";
import { SingleIconLabel } from "./SingleIconLabel";

import style from "./EmailInvitationForm.module.css";

export interface EmailInvitationFormHandle {
  focus: () => void;
  blur: () => void;
  clearForm: () => void;
}

interface EmailInvitationProps<TRoles extends readonly [string, ...string[]]> {
  roles: TRoles;
  roleDisplayName: (role: TRoles[number]) => string;
  isPending?: boolean;
  isDisabled?: boolean;
  defaultRole: TRoles[number];
  invalidEmailList: string[];
  isError?: boolean;
  onSubmit: (data: { emails: string[]; role: TRoles[number] }) => Promise<unknown>;

  onEmailAdded: () => void;
  onInvalidEmailAdded: () => void;
  onEmailRemoved: () => void;
  onRoleChanged: (newRole: TRoles[number], oldRole: TRoles[number]) => void;
  onInvitedPressed: (role: TRoles[number]) => void;
  proRoles?: Set<TRoles[number]>;
  isProRoleEligible?: (role: TRoles[number]) => boolean;
}

const EmailInvitationFormRender = <TRoles extends readonly [string, ...string[]]>(
  {
    roles,
    roleDisplayName,
    isPending,
    isDisabled,
    defaultRole,
    invalidEmailList,
    isError,
    onSubmit,
    onEmailAdded,
    onInvalidEmailAdded,
    onEmailRemoved,
    onRoleChanged,
    onInvitedPressed,
    proRoles = new Set(),
    isProRoleEligible,
  }: EmailInvitationProps<TRoles>,
  ref: React.ForwardedRef<EmailInvitationFormHandle>
) => {
  const [currentInputValue, setCurrentInputValue] = useState("");
  const [isCurrentInputValid, setIsCurrentInputValid] = useState(false);
  const tagInputRef = useRef<TagInputRef | null>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      tagInputRef.current?.focus();
    },
    blur: () => {
      tagInputRef.current?.blur();
    },
    clearForm: () => {
      setValue("emails", []);
      setCurrentInputValue("");
      setIsCurrentInputValid(false);
      tagInputRef.current?.clearInput();
    },
  }));

  const emailFormSchema = useMemo(
    () =>
      z.object({
        emails: z.array(z.string()),
        role: z.enum(roles),
      }),
    [roles]
  );

  type EmailFormValues = {
    emails: string[];
    role: string;
  };

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
    watch,
  } = useForm<EmailFormValues>({
    defaultValues: { emails: [], role: defaultRole },
    resolver: zodResolver(emailFormSchema),
  });

  const emailsWatch = watch("emails", []);
  const roleWatch = watch("role", defaultRole);

  const invalidEmails = useMemo(() => {
    const invalidEmails = new Set<string>();
    for (const email of invalidEmailList) invalidEmails.add(email);
    return invalidEmails;
  }, [invalidEmailList]);

  const { validEmails, invalidTags } = useMemo((): {
    validEmails: string[];
    invalidTags: string[];
  } => {
    if (!emailsWatch.length) {
      return { validEmails: [], invalidTags: [] };
    }

    const invalidSetForRenderedTags = new Set<string>();

    for (const email of emailsWatch) {
      if (isEmailInvalid(email) || invalidEmails.has(email)) {
        invalidSetForRenderedTags.add(email);
      }
    }

    const valid = emailsWatch.filter((e) => !invalidSetForRenderedTags.has(e));
    return { validEmails: valid, invalidTags: Array.from(invalidSetForRenderedTags) };
  }, [emailsWatch, invalidEmails]);

  const handleOnEmailAdded = useCallback(
    (email: string) => {
      setCurrentInputValue("");
      setIsCurrentInputValid(false);
      const isEmailValid = !isEmailInvalid(email) && !invalidEmails.has(email);
      if (isEmailValid) {
        onEmailAdded();
      } else {
        onInvalidEmailAdded();
      }
    },
    [onEmailAdded, onInvalidEmailAdded, invalidEmails]
  );

  const submit = useCallback(async () => {
    const emailsSet = new Set([...validEmails]);

    const trimmedInput = currentInputValue.trim();
    if (trimmedInput && !emailsSet.has(trimmedInput))
      setValue("emails", [...emailsWatch, trimmedInput]);
    if (isCurrentInputValid && trimmedInput !== "") {
      handleOnEmailAdded(trimmedInput);
      emailsSet.add(trimmedInput);
    }

    const emailsToSubmit = Array.from(emailsSet);
    if (proRoles.has(roleWatch) && !isProRoleEligible?.(roleWatch)) {
      return;
    }

    tagInputRef.current?.clearInput();
    await onSubmit({ emails: emailsToSubmit, role: roleWatch });

    setTimeout(() => {
      tagInputRef.current?.focus();
    }, 0);
  }, [
    validEmails,
    currentInputValue,
    setValue,
    proRoles,
    isProRoleEligible,
    emailsWatch,
    isCurrentInputValid,
    onSubmit,
    roleWatch,
    handleOnEmailAdded,
  ]);

  const handleInputChange = useCallback(
    (value: string) => {
      setCurrentInputValue(value);
      const isInInvalidList = invalidEmails.has(value.trim());
      setIsCurrentInputValid(!isEmailInvalid(value.trim()) && !isInInvalidList);
    },
    [invalidEmails]
  );

  const isSubmitDisabled = useMemo(() => {
    const hasInvalidInput = currentInputValue.trim() !== "" && !isCurrentInputValid;

    const hasValidEmails = validEmails.length > 0;
    const hasValidInput = isCurrentInputValid && currentInputValue.trim() !== "";

    return isPending || hasInvalidInput || (!hasValidEmails && !hasValidInput) || isSubmitting;
  }, [currentInputValue, isCurrentInputValid, validEmails.length, isPending, isSubmitting]);

  useEffect(() => {
    setValue("role", defaultRole);
  }, [defaultRole, setValue]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className={style.formContainer}>
        <Controller
          control={control}
          name="emails"
          render={({ field: { name, onBlur, onChange, value } }) => (
            <TagInput
              ref={tagInputRef}
              className={style.tagInput}
              values={value}
              disabled={isPending}
              name={name}
              placeholder="Emails"
              maxRows={4}
              onBlur={(_emails) => {
                onBlur();
              }}
              onChange={onChange}
              errors={invalidTags}
              onRemove={onEmailRemoved}
              onAdd={handleOnEmailAdded}
              onInput={(e) => handleInputChange(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                const target = e.currentTarget as HTMLInputElement;
                const isEmpty = target.value === "";
                e.preventDefault();
                if (isEmpty && validEmails.length > 0) {
                  handleSubmit(submit)();
                }
              }}
            />
          )}
        />
        <div className={style.formButtonsContainer}>
          <Menu
            activator={
              <SelectActivator
                aria-label="Role"
                disabled={isPending}
                label={roleDisplayName(roleWatch)}
                variant="grayscale"
              />
            }
          >
            {roles.map((role) => (
              <Menu.Item
                key={role}
                closeOnClick
                onClick={(event) => {
                  event.preventDefault();

                  if (role === roleWatch) {
                    return;
                  }

                  setValue("role", role);
                  onRoleChanged(role, roleWatch);
                }}
              >
                <SingleIconLabel
                  icon={role === roleWatch ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />}
                  label={roleDisplayName(role)}
                />
              </Menu.Item>
            ))}
          </Menu>

          <Button
            disabled={isSubmitDisabled || isDisabled}
            type="submit"
            onClick={() => {
              onInvitedPressed(roleWatch);
            }}
          >
            Invite
          </Button>
        </div>
        {isError && <span className={style.red}>{"Something went wrong. Please try again."}</span>}
      </div>
    </form>
  );
};

export const EmailInvitationForm = React.forwardRef(EmailInvitationFormRender) as <
  TRoles extends readonly [string, ...string[]],
>(
  props: EmailInvitationProps<TRoles> & React.RefAttributes<EmailInvitationFormHandle>
) => React.ReactElement;
